import { Component } from '../../core/component.js';
import { assistantApi } from './assistant-service.js';
import { escapeHtml } from '../../core/utils.js';
import { Toast } from '../ui/toast.js';
import { hasPermission } from '../../modules/auth/auth-service.js';

const WELCOME_SUGGESTIONS = [
    'Сводка за день',
    'Сколько записей сегодня?',
    'Выручка за месяц',
    'Какие услуги?',
    'Загрузка мастеров',
    'Последние звонки',
];

const INTENT_LABELS = {
    appointments_count: 'Записи',
    appointments_list: 'Список записей',
    revenue_summary: 'Финансы',
    clients_inactive: 'Клиенты',
    clients_count: 'Клиенты',
    staff_workload: 'Мастера',
    low_stock: 'Склад',
    book_appointment: 'Запись',
    today_summary: 'Сводка',
    call_analytics: 'Звонки',
    calls_list: 'Список звонков',
    services_list: 'Услуги',
    client_search: 'Клиент',
    capabilities: 'Справка',
    general_question: 'Общий',
    unknown: 'Ответ',
    agentic: 'Анализ',
    error: 'Ошибка',
};

const INTENT_ICONS = {
    appointments_count: '📅',
    appointments_list: '📋',
    revenue_summary: '💰',
    clients_inactive: '👥',
    clients_count: '👥',
    staff_workload: '👩‍💼',
    low_stock: '📦',
    book_appointment: '✏️',
    today_summary: '📊',
    call_analytics: '📞',
    calls_list: '📞',
    services_list: '💇',
    client_search: '🔍',
    capabilities: '💡',
    general_question: '💬',
    agentic: '🔬',
    error: '⚠️',
};

export class AssistantPanel extends Component {
    constructor() {
        super();
        this.state = {
            open: false,
            messages: [],
            input: '',
            loading: false,
            historyLoaded: false,
            expandedPrompt: null,
            agentic: false,
        };
        // Deep-analysis mode is gated server-side by dashboard.view_finance;
        // only surface the toggle to users who can actually use it.
        this._canAnalyze = hasPermission('dashboard.view_finance');
        this._onKeyDown = this._onKeyDown.bind(this);
        this._introShown = false;
        this._loadingPromptIdx = null;
        this._lastFailedMsg = null;
    }

    template() {
        const { open, messages, loading } = this.state;
        const hasNewHint = !this._introShown && messages.length === 0;

        const fabHtml = `
            <button class="assistant-fab ${hasNewHint && !open ? 'assistant-fab--pulse' : ''}" data-action="toggle" title="AI Ассистент (Ctrl+/)">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z"/>
                </svg>
            </button>
        `;

        if (!open) return fabHtml;

        const messagesHtml = messages.length === 0
            ? this._renderWelcome()
            : messages.map((m, i) => this._renderMessage(m, i)).join('');

        // Show suggestions from last bot message, or welcome suggestions
        const lastBotMsg = [...messages].reverse().find(m => m.role === 'assistant');
        const suggestions = lastBotMsg?.suggestions?.length
            ? lastBotMsg.suggestions
            : (messages.length === 0 ? WELCOME_SUGGESTIONS : []);

        const suggestionsHtml = suggestions.length && !loading
            ? `<div class="assistant-suggestions">${suggestions.map(s =>
                `<button class="assistant-suggestion" data-action="suggest" data-text="${escapeHtml(s)}">${escapeHtml(s)}</button>`
            ).join('')}</div>`
            : '';

        const modeHtml = this._canAnalyze
            ? `<div class="assistant-mode">
                <button class="assistant-mode__toggle ${this.state.agentic ? 'assistant-mode__toggle--on' : ''}" data-action="toggle-agentic" title="Агент комбинирует запросы к данным для глубокого анализа (медленнее, но отвечает на сложные вопросы)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 0 1-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 0 1 4.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0 1 12 15a9.065 9.065 0 0 0-6.23-.693L5 14.5m14.8.8 1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0 1 12 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5"/></svg>
                    <span>Глубокий анализ</span>
                </button>
            </div>`
            : '';

        const inputPlaceholder = this.state.agentic
            ? 'Задайте вопрос для анализа данных…'
            : 'Спросите что-нибудь...';

        const charCount = (this.state.input || '').length;
        const charCountHtml = charCount > 400
            ? `<span class="assistant-char-count ${charCount >= 500 ? 'assistant-char-count--limit' : ''}">${charCount}/500</span>`
            : '';

        return `
            ${fabHtml}
            <div class="assistant-panel">
                <div class="assistant-header">
                    <div class="assistant-header__title">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09Z"/>
                        </svg>
                        AI Ассистент
                        <span class="assistant-header__badge">beta</span>
                    </div>
                    <div class="assistant-header__actions">
                        <button class="assistant-header__btn" data-action="load-history" title="Загрузить историю">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
                            </svg>
                        </button>
                        <button class="assistant-header__btn" data-action="copy-all" title="Копировать диалог">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/>
                            </svg>
                        </button>
                        <button class="assistant-header__btn" data-action="clear" title="Очистить">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0"/>
                            </svg>
                        </button>
                        <button class="assistant-header__btn" data-action="close" title="Закрыть (Esc)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/>
                            </svg>
                        </button>
                    </div>
                </div>
                <div class="assistant-messages" id="assistantMessages">
                    ${messagesHtml}
                    ${loading ? this._renderTyping() : ''}
                </div>
                ${suggestionsHtml}
                ${modeHtml}
                <div class="assistant-input-area">
                    <div class="assistant-input-wrap">
                        <textarea
                            class="assistant-input"
                            id="assistantInput"
                            placeholder="${inputPlaceholder}"
                            maxlength="500"
                            rows="1"
                            autocomplete="off"
                            ${loading ? 'disabled' : ''}></textarea>
                        ${charCountHtml}
                    </div>
                    <button class="assistant-send" data-action="send" ${loading ? 'disabled' : ''} title="Отправить (Enter)">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"/>
                        </svg>
                    </button>
                </div>
            </div>
        `;
    }

    _renderWelcome() {
        return `
            <div class="assistant-welcome">
                <div class="assistant-welcome__icon">
                    <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary)" stroke-width="1.5">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z"/>
                    </svg>
                </div>
                <div class="assistant-welcome__title">AI Ассистент</div>
                <div class="assistant-welcome__text">Спросите о записях, клиентах, финансах или складе</div>
                <div class="assistant-welcome__categories">
                    <div class="assistant-welcome__cat">
                        <span class="assistant-welcome__cat-icon">📅</span>
                        <span>Записи и расписание</span>
                    </div>
                    <div class="assistant-welcome__cat">
                        <span class="assistant-welcome__cat-icon">💰</span>
                        <span>Финансы и выручка</span>
                    </div>
                    <div class="assistant-welcome__cat">
                        <span class="assistant-welcome__cat-icon">💇</span>
                        <span>Услуги и цены</span>
                    </div>
                    <div class="assistant-welcome__cat">
                        <span class="assistant-welcome__cat-icon">👥</span>
                        <span>Клиенты и мастера</span>
                    </div>
                    <div class="assistant-welcome__cat">
                        <span class="assistant-welcome__cat-icon">📞</span>
                        <span>Звонки и аналитика</span>
                    </div>
                </div>
            </div>
        `;
    }

    _renderMessage(msg, index) {
        const isUser = msg.role === 'user';
        const cls = isUser ? 'assistant-msg--user' : 'assistant-msg--bot';
        const content = isUser
            ? escapeHtml(msg.message)
            : (msg.isAI ? this._formatAgentic(msg.message) : this._formatAnswer(msg.message));

        const aiMetaHtml = (!isUser && msg.isAI && (msg.toolCount != null || msg.cost != null))
            ? `<div class="assistant-msg__ai-meta" title="Обращений к данным салона${msg.cost ? ' · стоимость запроса' : ''}">🔬 анализ${msg.toolCount ? ` · ${msg.toolCount}×` : ''}${msg.cost ? ` · ${Number(msg.cost).toFixed(1)} ₽` : ''}</div>`
            : '';

        const time = msg.time || '';
        const timeHtml = time ? `<span class="assistant-msg__time">${escapeHtml(time)}</span>` : '';

        const intentIcon = !isUser && msg.intent && INTENT_ICONS[msg.intent] || '';
        const intentLabel = !isUser && msg.intent && INTENT_LABELS[msg.intent]
            ? `<span class="assistant-msg__intent">${intentIcon ? intentIcon + ' ' : ''}${escapeHtml(INTENT_LABELS[msg.intent])}</span>`
            : '';

        const copyBtn = `<button class="assistant-msg__copy" data-action="copy" data-index="${index}" title="Копировать">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/>
            </svg>
        </button>`;

        // Prompt button: only on bot messages with messageId
        const hasPrompt = !isUser && (msg.messageId || msg.promptText);
        const isExpanded = this.state.expandedPrompt === index;
        const isLoadingPrompt = this._loadingPromptIdx === index;
        const promptBtn = hasPrompt
            ? `<button class="assistant-msg__prompt-toggle ${isExpanded ? 'assistant-msg__prompt-toggle--active' : ''}" data-action="toggle-prompt" data-index="${index}" title="Исходный запрос к LLM">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5"/>
                </svg>
                <span>${isLoadingPrompt ? '...' : ''}</span>
            </button>`
            : '';

        const promptBlock = msg.promptText && isExpanded
            ? `<div class="assistant-msg__prompt-text">
                <div class="assistant-msg__prompt-header">
                    <span>Исходный запрос для LLM</span>
                    <button class="assistant-msg__prompt-copy" data-action="copy-prompt" data-index="${index}" title="Копировать">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9.75a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"/>
                        </svg>
                    </button>
                </div>
                <pre class="assistant-msg__prompt-body">${escapeHtml(msg.promptText)}</pre>
            </div>`
            : '';

        // Error state with retry
        const isError = msg.intent === 'error' && !isUser;
        const retryBtn = isError && this._lastFailedMsg
            ? `<button class="assistant-msg__retry" data-action="retry" title="Повторить">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182"/>
                </svg>
                Повторить
            </button>`
            : '';

        const metaHtml = (intentLabel || timeHtml || promptBtn)
            ? `<div class="assistant-msg__meta">${intentLabel}${promptBtn}${timeHtml}</div>`
            : '';

        return `<div class="assistant-msg ${cls} assistant-msg--animate ${isError ? 'assistant-msg--error' : ''}">
            <div class="assistant-msg__content">${content}</div>
            ${aiMetaHtml}
            ${metaHtml}
            ${retryBtn}
            ${promptBlock}
            ${copyBtn}
        </div>`;
    }

    /**
     * Rich formatter for agentic answers: inline markdown (bold/bullets/emoji)
     * plus markdown-table → HTML-table rendering. Content is HTML-escaped by
     * _formatAnswer before any tags are injected, so table cells are safe.
     */
    _formatAgentic(text) {
        if (!text) return '';
        const segments = this._formatAnswer(text).split('<br>');
        let out = '';
        let buf = [];
        const flush = () => {
            if (!buf.length) return;
            // Drop the markdown separator row (|---|---|)
            const rows = buf.filter(r => !/^\|[\s\-:|]+\|$/.test(r.trim().replace(/<[^>]+>/g, '')));
            if (buf.length >= 2 && rows.length) {
                out += '<table class="ai-table">';
                rows.forEach((row, i) => {
                    const cells = row.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
                    const tag = i === 0 ? 'th' : 'td';
                    out += '<tr>' + cells.map(c => `<${tag}>${c}</${tag}>`).join('') + '</tr>';
                });
                out += '</table>';
            } else {
                out += buf.join('<br>') + '<br>';
            }
            buf = [];
        };
        for (const seg of segments) {
            if (/^\|.*\|/.test(seg.trim())) {
                buf.push(seg);
            } else {
                flush();
                out += seg + '<br>';
            }
        }
        flush();
        return `<div class="ai-response">${out}</div>`;
    }

    _renderTyping() {
        return `
            <div class="assistant-msg assistant-msg--bot assistant-msg--typing">
                <span class="assistant-typing-dot"></span>
                <span class="assistant-typing-dot"></span>
                <span class="assistant-typing-dot"></span>
                <span class="assistant-typing-label">Думаю...</span>
            </div>
        `;
    }

    _formatAnswer(text) {
        if (!text) return '';
        return escapeHtml(text)
            .replace(/\n/g, '<br>')
            .replace(/✓/g, '<span class="text-success">✓</span>')
            .replace(/✗/g, '<span class="text-danger">✗</span>')
            .replace(/⚠️/g, '<span class="text-warning">⚠️</span>')
            .replace(/⭐/g, '<span class="assistant-star">⭐</span>')
            .replace(/•/g, '<span class="assistant-bullet">•</span>')
            .replace(/(\d+)\.\s/g, '<span class="assistant-num">$1.</span> ')
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    }

    afterRender() {
        // Toggle panel
        this.on('[data-action="toggle"]', 'click', () => {
            this.state.open = !this.state.open;
            this.render();
            if (this.state.open) {
                this._focusInput();
                if (!this._introShown && this.state.messages.length === 0) {
                    this._introShown = true;
                    this.state.input = 'что ты умеешь?';
                    this._send();
                }
            }
        });

        this.on('[data-action="close"]', 'click', () => {
            this.state.open = false;
            this.render();
        });

        this.on('[data-action="clear"]', 'click', () => {
            this.state.messages = [];
            this.state.expandedPrompt = null;
            this._lastFailedMsg = null;
            this.render();
            this._focusInput();
        });

        this.on('[data-action="load-history"]', 'click', () => this._loadHistory());

        this.on('[data-action="copy"]', 'click', (e) => {
            const idx = parseInt(e.target.closest('[data-index]')?.dataset.index, 10);
            const msg = this.state.messages[idx];
            if (msg) {
                this._copyText(msg.message);
                this._showCopyFeedback(e.target.closest('[data-action="copy"]'));
            }
        });

        this.on('[data-action="copy-all"]', 'click', () => {
            const text = this.state.messages
                .map(m => (m.role === 'user' ? 'Вопрос: ' : 'Ответ: ') + m.message)
                .join('\n\n');
            if (text) {
                this._copyText(text);
                Toast.success('Диалог скопирован');
            }
        });

        // Toggle prompt text
        this.on('[data-action="toggle-prompt"]', 'click', async (e) => {
            const idx = parseInt(e.target.closest('[data-index]')?.dataset.index, 10);
            const msg = this.state.messages[idx];
            if (!msg) return;

            if (this.state.expandedPrompt === idx) {
                this.state.expandedPrompt = null;
                this.render();
                return;
            }

            if (msg.promptText) {
                this.state.expandedPrompt = idx;
                this.render();
                return;
            }

            if (!msg.messageId) return;
            this._loadingPromptIdx = idx;
            this.render();

            try {
                const res = await assistantApi.prompt(msg.messageId);
                msg.promptText = res.prompt_text || 'Промпт не найден';
            } catch {
                msg.promptText = 'Не удалось загрузить промпт';
            }
            this._loadingPromptIdx = null;
            this.state.expandedPrompt = idx;
            this.render();
        });

        this.on('[data-action="copy-prompt"]', 'click', (e) => {
            const idx = parseInt(e.target.closest('[data-index]')?.dataset.index, 10);
            const msg = this.state.messages[idx];
            if (msg?.promptText) {
                this._copyText(msg.promptText);
                Toast.success('Запрос скопирован');
            }
        });

        this.on('[data-action="send"]', 'click', () => this._send());

        // Toggle deep-analysis (agentic) mode
        this.on('[data-action="toggle-agentic"]', 'click', () => {
            this.state.agentic = !this.state.agentic;
            this.render();
            this._focusInput();
        });

        // Retry failed message
        this.on('[data-action="retry"]', 'click', () => {
            if (this._lastFailedMsg) {
                // Remove the error message
                const lastIdx = this.state.messages.length - 1;
                if (this.state.messages[lastIdx]?.intent === 'error') {
                    this.state.messages.pop();
                }
                this.state.input = this._lastFailedMsg;
                this._lastFailedMsg = null;
                this._send();
            }
        });

        this.on('[data-action="suggest"]', 'click', (e) => {
            const text = e.target.closest('[data-text]')?.dataset.text;
            if (text) {
                this.state.input = text;
                this._send();
            }
        });

        // Textarea auto-resize + Enter to send
        const input = this.el.querySelector('#assistantInput');
        if (input) {
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    this._send();
                }
            });
            input.addEventListener('input', () => {
                this.state.input = input.value;
                input.style.height = 'auto';
                input.style.height = Math.min(input.scrollHeight, 120) + 'px';
                // Update char counter without full re-render
                const counter = this.el.querySelector('.assistant-char-count');
                const len = input.value.length;
                if (len > 400) {
                    if (!counter) {
                        const wrap = input.closest('.assistant-input-wrap');
                        if (wrap) {
                            const span = document.createElement('span');
                            span.className = `assistant-char-count ${len >= 500 ? 'assistant-char-count--limit' : ''}`;
                            span.textContent = `${len}/500`;
                            wrap.appendChild(span);
                        }
                    } else {
                        counter.textContent = `${len}/500`;
                        counter.classList.toggle('assistant-char-count--limit', len >= 500);
                    }
                } else if (counter) {
                    counter.remove();
                }
            });
            if (this.state.input) input.value = this.state.input;
        }

        this._scrollToBottom();
    }

    mount(container) {
        super.mount(container);
        document.addEventListener('keydown', this._onKeyDown);
        return this;
    }

    destroy() {
        document.removeEventListener('keydown', this._onKeyDown);
        super.destroy();
    }

    _onKeyDown(e) {
        if ((e.ctrlKey || e.metaKey) && e.key === '/') {
            e.preventDefault();
            this.state.open = !this.state.open;
            this.render();
            if (this.state.open) this._focusInput();
        }
        if (e.key === 'Escape' && this.state.open) {
            this.state.open = false;
            this.render();
        }
    }

    async _send() {
        const input = this.el.querySelector('#assistantInput');
        const message = (input?.value || this.state.input || '').trim();
        if (!message || this.state.loading) return;

        const now = new Date();
        const time = now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

        this.state.input = '';
        this._lastFailedMsg = message;
        this.state.messages.push({ role: 'user', message, time });
        this.state.loading = true;
        this.render();

        const agentic = this.state.agentic && this._canAnalyze;
        try {
            const res = agentic
                ? await assistantApi.analyze(message)
                : await assistantApi.chat(message);

            this.state.messages.push({
                role: 'assistant',
                message: res.answer || 'Нет ответа',
                intent: res.intent,
                isAI: agentic,
                cost: agentic ? (res.data?.cost ?? null) : null,
                toolCount: agentic ? (res.data?.tool_calls?.length ?? null) : null,
                suggestions: res.suggestions || [],
                messageId: res.message_id || null,
                promptText: null,
                time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            });
            this._lastFailedMsg = null;
        } catch (err) {
            this.state.messages.push({
                role: 'assistant',
                message: err.message || 'Не удалось получить ответ. Попробуйте позже.',
                intent: 'error',
                suggestions: ['Сводка за день'],
                time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
            });
        } finally {
            this.state.loading = false;
            this.render();
            this._focusInput();
        }
    }

    _focusInput() {
        requestAnimationFrame(() => {
            this.el.querySelector('#assistantInput')?.focus();
        });
    }

    async _loadHistory() {
        if (this.state.loading) return;
        this.state.loading = true;
        this.render();

        try {
            const res = await assistantApi.history(1, 50);
            const items = Array.isArray(res) ? res : (res.data || res);
            if (Array.isArray(items) && items.length > 0) {
                this.state.messages = items.reverse().map(m => ({
                    role: m.role,
                    message: m.message,
                    intent: m.intent || null,
                    suggestions: [],
                    messageId: m.id || null,
                    promptText: null,
                    time: m.created_at ? new Date(m.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '',
                }));
                Toast.info(`Загружено ${items.length} сообщений`);
            } else {
                this.state.messages = [{
                    role: 'assistant',
                    message: 'История запросов пуста.',
                    suggestions: WELCOME_SUGGESTIONS,
                    time: new Date().toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
                }];
            }
        } catch {
            Toast.error('Не удалось загрузить историю');
        } finally {
            this.state.loading = false;
            this.render();
            this._scrollToBottom();
        }
    }

    async _copyText(text) {
        try {
            await navigator.clipboard.writeText(text);
        } catch {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.style.cssText = 'position:fixed;left:-9999px';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            ta.remove();
        }
    }

    _showCopyFeedback(btn) {
        if (!btn) return;
        btn.classList.add('assistant-msg__copy--done');
        setTimeout(() => btn.classList.remove('assistant-msg__copy--done'), 1500);
    }

    _scrollToBottom() {
        requestAnimationFrame(() => {
            const container = this.el.querySelector('#assistantMessages');
            if (container) {
                container.scrollTop = container.scrollHeight;
            }
        });
    }
}
